import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUSES = [
  { id: "pending", label: "Pending" },
  { id: "active", label: "Active" },
  { id: "rejected", label: "Rejected" },
  { id: "unsupported", label: "Unsupported" },
];

const EARNKARO_CREATE = "https://earnkaro.com/create-earn-link";

function when(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

async function downloadCsv(status) {
  const response = await api.get("/admin/earnkaro/pending", {
    params: { format: "csv", status },
    responseType: "blob",
  });
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = status === "pending" ? "earnkaro-pending.csv" : `earnkaro-${status}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function EarnKaroQueue() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [busyId, setBusyId] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const copyTimer = useRef(0);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-earnkaro", status],
    queryFn: async () => (await api.get("/admin/earnkaro/pending", { params: { status } })).data,
  });
  const links = data?.links || [];

  useEffect(() => () => window.clearTimeout(copyTimer.current), []);

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-earnkaro"] });

  const copyUrl = async (row) => {
    try {
      await navigator.clipboard.writeText(row.original_url || "");
      setCopiedId(row.id);
      window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopiedId((current) => (current === row.id ? "" : current)), 2000);
    } catch {
      setErrors((prev) => ({ ...prev, [row.id]: "Could not copy that URL. Select it and copy it manually." }));
    }
  };

  const submit = async (row, body) => {
    setBusyId(row.id);
    setErrors((prev) => ({ ...prev, [row.id]: "" }));
    try {
      await api.put(`/admin/earnkaro/links/${row.id}`, body);
      setDrafts((prev) => ({ ...prev, [row.id]: "" }));
      await refresh();
    } catch (err) {
      setErrors((prev) => ({ ...prev, [row.id]: apiError(err) }));
    } finally {
      setBusyId("");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    try {
      await downloadCsv(status);
    } catch (err) {
      setExportError(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mb-8" data-testid="earnkaro-queue">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl tracking-tight">EarnKaro queue</div>
          <p className="text-xs text-aura-muted mt-1 max-w-2xl leading-relaxed">
            Shop now already sends Flipkart, JioMart, and the other partner stores through Cuelinks when that campaign is approved. Paste a short link here only when you want that one product to use EarnKaro instead.
          </p>
        </div>
        <button
          type="button"
          onClick={exportCsv}
          disabled={exporting}
          className="px-4 py-1.5 border border-aura-border text-xs rounded-full disabled:opacity-60 inline-flex items-center gap-2"
          data-testid="earnkaro-export"
        >
          {exporting && <Loader2 className="w-3 h-3 animate-spin" />}
          Export CSV
        </button>
      </div>
      {exportError && (
        <p className="text-sm text-aura-accent mb-3" data-testid="earnkaro-export-error">{exportError}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-4" role="group" aria-label="EarnKaro status">
        {STATUSES.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setStatus(item.id)}
            className={`px-3 py-1 text-xs rounded-full border ${status === item.id ? "btn-accent border-transparent" : "border-aura-border text-aura-muted"}`}
            data-testid={`earnkaro-status-${item.id}`}
            aria-pressed={status === item.id}
          >
            {item.label}
          </button>
        ))}
      </div>

      {isLoading && <div className="h-40 shimmer mb-4" data-testid="earnkaro-loading" />}
      {!isLoading && error && (
        <div className="border border-aura-border px-5 py-4 text-sm text-aura-accent mb-4" data-testid="earnkaro-load-error">
          {apiError(error)}
        </div>
      )}
      {!isLoading && !error && links.length === 0 && (
        <div className="border border-aura-border px-5 py-8 text-sm text-aura-muted" data-testid="earnkaro-empty">
          Items appear as shoppers search partner stores (Flipkart, Myntra, Nykaa, JioMart, and others).
          Nothing is in this {status} list yet.
        </div>
      )}
      {!isLoading && !error && links.length > 0 && (
        <div className="border border-aura-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-luxe text-aura-muted">
              <tr className="border-b border-aura-border text-left">
                <th className="px-4 py-2 font-medium">Store</th>
                <th className="px-4 py-2 font-medium">Rate</th>
                <th className="px-4 py-2 font-medium">Product URL</th>
                <th className="px-4 py-2 font-medium">First seen</th>
                <th className="px-4 py-2 font-medium">Clicks</th>
                <th className="px-4 py-2 font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {links.map((row) => {
                const pending = (row.status || status) === "pending";
                const saving = busyId === row.id;
                return (
                  <tr key={row.id} className="border-b border-aura-border align-top" data-testid={`earnkaro-row-${row.id}`}>
                    <td className="px-4 py-3 font-medium whitespace-nowrap" data-testid={`earnkaro-store-${row.id}`}>{row.store || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-aura-muted" data-testid={`earnkaro-rate-${row.id}`}>{row.rate || "—"}</td>
                    <td className="px-4 py-3 min-w-[16rem]">
                      <a
                        href={row.original_url}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all link-underline"
                        data-testid={`earnkaro-url-${row.id}`}
                      >
                        {row.original_url}
                      </a>
                      <div className="flex flex-wrap items-center gap-3 mt-2">
                        <button
                          type="button"
                          onClick={() => copyUrl(row)}
                          className="inline-flex items-center gap-1 text-[11px] text-aura-muted hover:text-aura-ink"
                          data-testid={`earnkaro-copy-${row.id}`}
                        >
                          <Copy className="w-3 h-3" />
                          {copiedId === row.id ? "Copied" : "Copy"}
                        </button>
                        <a
                          href={EARNKARO_CREATE}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] link-underline"
                          data-testid={`earnkaro-open-${row.id}`}
                        >
                          Open in EarnKaro <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-aura-muted" data-testid={`earnkaro-seen-${row.id}`}>{when(row.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`earnkaro-clicks-${row.id}`}>{row.click_count || 0}</td>
                    <td className="px-4 py-3 min-w-[16rem]">
                      {pending ? (
                        <div>
                          <div className="flex flex-wrap gap-2">
                            <input
                              type="url"
                              inputMode="url"
                              placeholder="https://fktr.in/…"
                              value={drafts[row.id] || ""}
                              onChange={(event) => setDrafts((prev) => ({ ...prev, [row.id]: event.target.value }))}
                              className="flex-1 min-w-[12rem] bg-aura-surface border border-aura-border px-3 py-1.5 text-xs outline-none"
                              data-testid={`earnkaro-short-${row.id}`}
                              aria-label={`EarnKaro short link for ${row.store || "this product"}`}
                              autoComplete="off"
                              spellCheck={false}
                            />
                            <button
                              type="button"
                              onClick={() => submit(row, { affiliate_url: (drafts[row.id] || "").trim() })}
                              disabled={saving || !(drafts[row.id] || "").trim()}
                              className="px-3 py-1.5 btn-accent text-xs rounded-full disabled:opacity-50 inline-flex items-center gap-1"
                              data-testid={`earnkaro-save-${row.id}`}
                            >
                              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
                              Save
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => submit(row, { status: "unsupported" })}
                              disabled={saving}
                              className="px-3 py-1 text-[11px] border border-aura-border rounded-full disabled:opacity-50"
                              data-testid={`earnkaro-unsupported-${row.id}`}
                            >
                              Unsupported
                            </button>
                            <button
                              type="button"
                              onClick={() => submit(row, { status: "rejected" })}
                              disabled={saving}
                              className="px-3 py-1 text-[11px] border border-aura-border rounded-full disabled:opacity-50"
                              data-testid={`earnkaro-rejected-${row.id}`}
                            >
                              Rejected
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs break-all" data-testid={`earnkaro-affiliate-${row.id}`}>
                          {row.affiliate_url || row.status || "—"}
                        </div>
                      )}
                      {errors[row.id] && (
                        <p className="text-xs text-aura-accent mt-2" data-testid={`earnkaro-error-${row.id}`}>{errors[row.id]}</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
